import { useState, useEffect } from 'react';
import { useParams } from 'react-router';
import { getCitiesByProject, type DataRow } from '@/lib/api';
import { Database, Download, Search, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/app/components/ui/table';
import { Badge } from '@/app/components/ui/badge';
import { objectsToCSV, downloadCSV } from '@/lib/csv';
import { toast } from 'sonner';

const HIDDEN_COLS = new Set(['id', 'project_id']);

function getColumns(rows: DataRow[]): string[] {
  if (rows.length === 0) return [];
  return Object.keys(rows[0]).filter((k) => !HIDDEN_COLS.has(k));
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'number') return value.toLocaleString();
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function inferType(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'object') return 'object';
  // Try to detect numeric strings
  if (typeof value === 'string' && value !== '' && !isNaN(Number(value))) return 'number';
  return 'text';
}

const TYPE_COLORS: Record<string, string> = {
  text:    'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  number:  'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
  boolean: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
  object:  'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20',
  null:    'bg-slate-500/10 text-slate-500 border-slate-500/20',
};

export default function CitiesView() {
  const { projectId } = useParams<{ projectId: string }>();
  const [rows, setRows] = useState<DataRow[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [schemaOpen, setSchemaOpen] = useState(true);

  useEffect(() => {
    if (!projectId) return;
    setIsLoading(true);
    getCitiesByProject(projectId)
      .then(setRows)
      .catch((err) => {
        console.error('Error loading data:', err);
        toast.error('Failed to load data');
      })
      .finally(() => setIsLoading(false));
  }, [projectId]);

  const columns = getColumns(rows);
  const sampleRow = rows[0] ?? null;

  const filteredRows = searchQuery
    ? rows.filter((row) =>
        columns.some((col) => {
          const val = row[col];
          return typeof val === 'string' && val.toLowerCase().includes(searchQuery.toLowerCase());
        })
      )
    : rows;

  const handleExportCSV = () => {
    const exportData = rows.map(({ id, project_id, ...rest }) => rest);
    downloadCSV(`data_${new Date().toISOString().split('T')[0]}.csv`, objectsToCSV(exportData));
    toast.success('Data exported successfully');
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Database className="size-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold">Data</h1>
            <p className="text-muted-foreground">
              {rows.length} {rows.length === 1 ? 'row' : 'rows'} · read-only
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={handleExportCSV} disabled={rows.length === 0}>
          <Download className="size-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Database className="size-16 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold">Loading data…</h3>
          </CardContent>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Database className="size-16 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No data found</h3>
            <p className="text-muted-foreground text-center max-w-md">
              This project's source table appears to be empty.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Schema Inspector */}
          <Card>
            <CardHeader className="pb-3">
              <button
                className="flex items-center justify-between w-full text-left"
                onClick={() => setSchemaOpen((v) => !v)}
              >
                <div>
                  <CardTitle className="text-base">Schema</CardTitle>
                  <CardDescription>{columns.length} columns</CardDescription>
                </div>
                {schemaOpen
                  ? <ChevronUp className="size-4 text-muted-foreground" />
                  : <ChevronDown className="size-4 text-muted-foreground" />
                }
              </button>
            </CardHeader>
            {schemaOpen && (
              <CardContent className="pt-0">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {columns.map((col) => {
                    const sample = sampleRow ? sampleRow[col] : undefined;
                    const type = inferType(sample);
                    return (
                      <div key={col} className="flex items-start gap-2 p-2 rounded-md border bg-muted/30">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-mono font-medium truncate">{col}</p>
                          <p className="text-xs text-muted-foreground truncate mt-0.5">
                            {sample !== null && sample !== undefined ? formatValue(sample) : '—'}
                          </p>
                        </div>
                        <Badge
                          variant="outline"
                          className={`text-xs shrink-0 ${TYPE_COLORS[type] ?? TYPE_COLORS.text}`}
                        >
                          {type}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            )}
          </Card>

          {/* Data Table */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Source Data</CardTitle>
                  <CardDescription>{rows.length} rows · showing first 200</CardDescription>
                </div>
              </div>
              <div className="relative mt-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                <Input
                  placeholder="Search all fields…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-10"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                  >
                    <X className="size-4 text-muted-foreground hover:text-foreground" />
                  </button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {columns.map((col) => (
                        <TableHead key={col} className="whitespace-nowrap">{col}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={columns.length} className="text-center text-muted-foreground py-8">
                          No rows match your search
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredRows.slice(0, 200).map((row) => (
                        <TableRow key={row.id}>
                          {columns.map((col) => (
                            <TableCell key={col} className="whitespace-nowrap">
                              {formatValue(row[col])}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
              {filteredRows.length > 200 && (
                <p className="text-sm text-muted-foreground mt-4">
                  Showing first 200 of {filteredRows.length} rows
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
